const API_BASE = '';

export class ApiError extends Error {
  status: number;
  detail?: string;
  bodyText?: string;
  constructor(message: string, status: number, detail?: string, bodyText?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.bodyText = bodyText;
  }
}

function looksLikeHtml(text: string): boolean {
  const trimmed = text.trim();
  return (
    trimmed.startsWith('<') ||
    /<\s*html[\s>]/i.test(trimmed) ||
    /<\s*head[\s>]/i.test(trimmed) ||
    /via_upstream/i.test(trimmed)
  );
}

export function getApiErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (err instanceof ApiError) {
    const detail = err.detail?.trim();
    if (detail && !looksLikeHtml(detail)) {
      return detail;
    }

    switch (err.status) {
      case 400:
        return 'The request was invalid. Check the file type and size (max 2 MB for images, 20 MB for animated GIF/PNG).';
      case 401:
        return 'Your session expired. Sign in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'This resource was not found.';
      case 413:
        return 'The file is too large. Images must be under 2 MB; animated GIF/PNG can be uploaded up to 20 MB.';
      case 500:
      case 502:
      case 503:
        return 'The server timed out or could not complete the request. Try again — extraction now runs in the background.';
      default:
        return fallback;
    }
  }

  if (err instanceof Error) {
    const message = err.message.trim();
    if (!message) {
      return fallback;
    }
    if (/failed to fetch|networkerror|load failed/i.test(message)) {
      return 'Could not reach the server. Check your connection and try again.';
    }
    return message;
  }

  return fallback;
}

type ApiInit = Omit<RequestInit, 'headers'> & { headers?: Record<string, string> };

export async function apiFetch(path: string, init?: ApiInit): Promise<Response> {
  const isJsonBody = init?.body != null && typeof init.body === 'string';
  return fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(isJsonBody ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  });
}

export async function apiJson<T>(path: string, init?: ApiInit): Promise<T> {
  const res = await apiFetch(path, init);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let detail: string | undefined;
    try {
      const body = text ? JSON.parse(text) : null;
      const d = body?.detail;
      if (typeof d === 'string') {
        detail = d;
      } else if (Array.isArray(d)) {
        detail = d.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ');
      } else {
        detail = text || undefined;
      }
    } catch {
      detail = text || undefined;
    }
    throw new ApiError(
      `Request failed: ${res.status} ${res.statusText}`,
      res.status,
      detail,
      text
    );
  }
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}
